import { ALL_SUPPORTED_CHAIN_IDS, SupportedChainId } from 'config/chains'
import { getChainInfo } from 'constants/chainInfo'
import { useConnection } from 'hooks/useConnection'
import useSelectChain from 'hooks/useSelectChain'
import { darken } from 'polished'
import { useRef } from 'react'
import { AlertTriangle, ArrowDownCircle, ChevronDown } from 'react-feather'
import { useCloseModal, useModalIsOpen, useOpenModal, useToggleModal } from 'state/application/hooks'
import { ApplicationModal } from 'state/application/reducer'
import styled from 'styled-components'
import { ExternalLink, MEDIA_WIDTHS } from 'theme'
import { opacify } from 'theme/utils'
import { isMobile } from 'utils/userAgent'

const ActiveRowLinkList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 8px;
  & > a {
    align-items: center;
    color: ${({ theme }) => theme.deprecated_text2};
    display: flex;
    flex-direction: row;
    font-size: 14px;
    font-weight: 500;
    justify-content: space-between;
    padding: 8px 0 4px;
    text-decoration: none;
  }
  & > a:first-child {
    margin: 0;
    margin-top: 0px;
    padding-top: 10px;
  }
`
const ActiveRowWrapper = styled.div`
  background-color: ${({ theme }) => theme.cardSecondary};
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  width: 100%;
`
const FlyoutHeader = styled.div`
  color: ${({ theme }) => theme.deprecated_text2};
  cursor: default;
  font-weight: 400;
`
const FlyoutMenu = styled.div<{ alignRight?: boolean }>`
  position: absolute;
  top: 50px;
  width: 272px;
  z-index: 99;
  padding-top: 10px;
  ${({ alignRight }) => (alignRight ? 'right: 0;' : 'left: 0;')}
  @media screen and (min-width: ${MEDIA_WIDTHS.upToSmall}px) {
    top: 35px;
  }
`
const FlyoutMenuContents = styled.div`
  align-items: flex-start;
  background-color: ${({ theme }) => theme.cardPrimary};
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.01), 0px 4px 8px rgba(0, 0, 0, 0.04), 0px 16px 24px rgba(0, 0, 0, 0.04),
    0px 24px 32px rgba(0, 0, 0, 0.01);
  border-radius: 8px;
  border: 2px solid ${({ theme }) => theme.cardBorderPrimary};
  display: flex;
  flex-direction: column;
  font-size: 16px;
  overflow: auto;
  padding: 16px;
  & > *:not(:last-child) {
    margin-bottom: 12px;
  }
`
const FlyoutRow = styled.div<{ active: boolean }>`
  align-items: center;
  background-color: ${({ active, theme }) => (active ? theme.cardSecondary : 'transparent')};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  font-weight: 500;
  gap: 8px;
  /* justify-content: space-between; */
  padding: 6px 8px;
  text-align: left;
  width: 100%;

  &:hover {
    background-color: ${({ theme }) => opacify(69, theme.cardSecondary)};
  }
`
const FlyoutRowActiveIndicator = styled.div`
  background-color: ${({ theme }) => theme.deprecated_green1};
  border-radius: 50%;
  height: 9px;
  width: 9px;
`

const CircleContainer = styled.div`
  width: 20px;
  display: flex;
  justify-content: center;
`

const LinkOutCircle = styled(ArrowDownCircle)`
  transform: rotate(230deg);
  width: 16px;
  height: 16px;
`
const Logo = styled.img`
  height: 20px;
  width: 20px;
  border-radius: 50%;
  /* margin-right: 8px; */
`
const NetworkLabel = styled.div`
  /* flex: 1 1 auto; */
`

const SelectorControls = styled.div<{ supportedChain: boolean }>`
  align-items: center;
  /* background-color: ${({ theme }) => theme.deprecated_bg0}; */
  /* border: 2px solid ${({ theme }) => theme.deprecated_bg0}; */
  border-radius: 16px;
  color: ${({ theme }) => theme.deprecated_text1};
  display: flex;
  font-weight: 500;
  justify-content: space-between;
  padding: 6px 8px;
  ${({ supportedChain, theme }) =>
    !supportedChain &&
    `
    color: ${theme.deprecated_white};
    background-color: ${theme.deprecated_red1};
    border: 2px solid ${theme.deprecated_red1};
  `}
  cursor: default;
  :focus {
    background-color: ${({ theme }) => darken(0.1, theme.deprecated_red1)};
  }
`
const SelectorLogo = styled(Logo)`
  /* @media screen and (min-width: ${MEDIA_WIDTHS.upToSmall}px) {
    margin-right: 8px;
  } */

  height: 24px;
  width: 24px;
  border: 1px solid ${({ theme }) => theme.borderSecondary};
  border-radius: 50%;
  /* overflow: hidden; */
  /* padding: 2px; */
  background: ${({ theme }) => theme.cardBorderPrimary};
`
const SelectorWrapper = styled.div`
  @media screen and (min-width: ${MEDIA_WIDTHS.upToSmall}px) {
    position: relative;
  }
`
const StyledChevronDown = styled(ChevronDown)`
  width: 16px;
`

const NetworkIcon = styled(AlertTriangle)`
  /* margin-left: 0.25rem; */
  /* margin-right: 0.25rem; */
  width: 16px;
  height: 16px;
`

const BridgeLabel = ({ chainId }: { chainId: SupportedChainId }) => {
  switch (chainId) {
    default:
      return <>Bridge</>
  }
}
const ExplorerLabel = ({ chainId }: { chainId: SupportedChainId }) => {
  switch (chainId) {
    case SupportedChainId.ROBINHOOD:
      return <>Etherscan</>
    default:
      return <>Berascan</>
  }
}

function Row({
  targetChain,
  onSelectChain,
}: {
  targetChain: SupportedChainId
  onSelectChain: (targetChain: number) => void
}) {
  const { chainId } = useConnection()
  if (!chainId) {
    return null
  }
  const active = chainId === targetChain
  const { helpCenterUrl, explorer, bridge, label, logoUrl } = getChainInfo(targetChain)

  const rowContent = (
    <FlyoutRow onClick={() => onSelectChain(targetChain)} active={active}>
      <Logo src={logoUrl} />
      <NetworkLabel>{label}</NetworkLabel>
      {chainId === targetChain && (
        <CircleContainer>
          <FlyoutRowActiveIndicator />
        </CircleContainer>
      )}
    </FlyoutRow>
  )

  if (active) {
    return (
      <ActiveRowWrapper>
        {rowContent}
        <ActiveRowLinkList>
          {bridge && (
            <ExternalLink href={bridge}>
              <BridgeLabel chainId={chainId} />
              <CircleContainer>
                <LinkOutCircle />
              </CircleContainer>
            </ExternalLink>
          )}
          {explorer && (
            <ExternalLink href={explorer}>
              <ExplorerLabel chainId={chainId} />
              <CircleContainer>
                <LinkOutCircle />
              </CircleContainer>
            </ExternalLink>
          )}
          {helpCenterUrl && (
            <ExternalLink href={helpCenterUrl}>
              <>Help Center</>
              <CircleContainer>
                <LinkOutCircle />
              </CircleContainer>
            </ExternalLink>
          )}
        </ActiveRowLinkList>
      </ActiveRowWrapper>
    )
  }
  return rowContent
}

export default function NetworkSelector({ menuAlignment = 'left' }: { menuAlignment?: 'left' | 'right' }) {
  const { chainId } = useConnection()

  const node = useRef<HTMLDivElement>(null)
  const isOpen = useModalIsOpen(ApplicationModal.NETWORK_SELECTOR)
  const openModal = useOpenModal(ApplicationModal.NETWORK_SELECTOR)
  const closeModal = useCloseModal(ApplicationModal.NETWORK_SELECTOR)
  const toggleModal = useToggleModal(ApplicationModal.NETWORK_SELECTOR)

  const info = getChainInfo(chainId)

  const selectChain = useSelectChain()

  if (!chainId) {
    return null
  }

  const onSupportedChain = info !== undefined
  if (!onSupportedChain) {
    return null
  }
  const alignRight = menuAlignment === 'right'

  return (
    <SelectorWrapper
      ref={node}
      onMouseEnter={isMobile ? undefined : openModal}
      onMouseLeave={isMobile ? undefined : closeModal}
    >
      <SelectorControls supportedChain={onSupportedChain} onClick={isMobile ? toggleModal : undefined}>
        <SelectorLogo src={info.logoUrl} />
      </SelectorControls>
      {isOpen && (
        <FlyoutMenu alignRight={alignRight}>
          <FlyoutMenuContents>
            <FlyoutHeader>
              <>Select a {!onSupportedChain ? ' supported ' : ''}network</>
            </FlyoutHeader>
            {ALL_SUPPORTED_CHAIN_IDS.map((chainId) => (
              <Row
                onSelectChain={async (targetChainId: SupportedChainId) => {
                  selectChain(targetChainId)
                  closeModal()
                }}
                targetChain={chainId}
                key={chainId}
              />
            ))}
          </FlyoutMenuContents>
        </FlyoutMenu>
      )}
    </SelectorWrapper>
  )
}
